/**
 * Seed Q&A section for Fellows Corner.
 *
 * Shows a short set of example questions with the mentor's answers as an
 * accordion, so a fellow immediately sees the kind of career questions the
 * feature is for (the "show example questions and responses" ask from the
 * 2026-07-16 Dr. Gao demo) when an environment has them configured. Each
 * answer is attributed to the mentor with their avatar and name, and offers
 * an "Ask your own version" affordance that prefills the ask form with that
 * question.
 *
 * Rendered inside the profile sidebar's "Questions" tab, so it uses the shared
 * `ProfileText` role components (item title, body, meta) rather than ad-hoc
 * type, to read consistently with the profile's own sections (per
 * `.claude/rules/profile.md`). The section is labeled by the tab, so it has no
 * heading of its own.
 *
 * The content comes entirely from `useFellowSeedQA` — see that hook for the
 * Flagsmith-authored source and the empty-list-means-no-tab contract.
 */

import { ChevronDown, ChevronRight } from 'lucide-react-native';
import { useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { primum_default_avatar } from '@/assets/images';
import {
  ProfileBodyText,
  ProfileItemMeta,
  ProfileItemTitle,
} from '@/components/profile/ProfileText';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionIcon,
  AccordionItem,
  AccordionTitleText,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import type { FellowSeedQA } from '@/constants/fellowsCorner';
import { segmentClient } from '@/util/analytics';

type FellowsSeedQuestionsProps = {
  /** Mentor id, attached to the toggle-tracking event. */
  mentorId: string;
  /** Full mentor name for attribution, e.g. "Dr. Jackson Gao". */
  mentorName: string;
  /** Mentor avatar; falls back to the default avatar when absent. */
  mentorAvatarSrc?: string;
  /** Seed Q&A attributed to this mentor. Renders nothing when empty. */
  items: readonly FellowSeedQA[];
  /** Prefills the ask form with the chosen question. */
  onAskQuestion: (question: string) => void;
};

export function FellowsSeedQuestions({
  mentorId,
  mentorName,
  mentorAvatarSrc,
  items,
  onAskQuestion,
}: FellowsSeedQuestionsProps) {
  // Multiple items stay open independently — opening one never collapses the
  // others. First item expanded by default so the section never reads empty.
  // The page keys this component by mentor id, so switching mentors remounts it
  // and resets the expanded set back to the first.
  const [expandedValues, setExpandedValues] = useState<string[]>(items.length ? ['qa-0'] : []);

  const handleExpandedChange = (nextValues: string[]) => {
    const toggled =
      nextValues.length > expandedValues.length
        ? { state: 'expanded' as const, value: nextValues.find((v) => !expandedValues.includes(v)) }
        : {
            state: 'collapsed' as const,
            value: expandedValues.find((v) => !nextValues.includes(v)),
          };

    if (toggled.value != null) {
      segmentClient
        .track(ANALYTICS_EVENTS.FELLOWS_QUESTION_TOGGLED, {
          mentor_id: mentorId,
          question_index: Number(toggled.value.replace('qa-', '')),
          state: toggled.state,
        })
        ?.catch(() => {});
    }

    setExpandedValues(nextValues);
  };

  if (!items.length) {
    return null;
  }

  return (
    <Accordion
      type="multiple"
      value={expandedValues}
      onValueChange={handleExpandedChange}
      className="bg-transparent shadow-none">
      {items.map((seedQA, index) => {
        const value = `qa-${index}`;

        return (
          <AccordionItem
            key={value}
            value={value}
            className={twJoin('border-outline-100', index < items.length - 1 && 'border-b')}>
            <AccordionHeader>
              <AccordionTrigger className="px-5 py-5 web:cursor-pointer">
                {({ isExpanded }: { isExpanded: boolean }) => (
                  <HStack className="w-full items-start justify-between gap-3">
                    <AccordionTitleText className="flex-1 text-left">
                      <ProfileItemTitle>{seedQA.question}</ProfileItemTitle>
                    </AccordionTitleText>
                    <AccordionIcon
                      as={isExpanded ? ChevronDown : ChevronRight}
                      className="mt-1 shrink-0 text-typography-500"
                    />
                  </HStack>
                )}
              </AccordionTrigger>
            </AccordionHeader>

            <AccordionContent className="px-5 pb-4">
              <VStack space="md">
                <HStack space="sm" className="items-center">
                  <Avatar size="xs" className="shrink-0">
                    <AvatarImage
                      source={mentorAvatarSrc ? { uri: mentorAvatarSrc } : primum_default_avatar}
                    />
                  </Avatar>
                  <ProfileItemMeta>{mentorName}</ProfileItemMeta>
                </HStack>

                <ProfileBodyText className="text-sm">{seedQA.answer}</ProfileBodyText>

                <Button
                  variant="link"
                  size="sm"
                  className="self-start"
                  onPress={() => onAskQuestion(seedQA.question)}>
                  <ButtonText>Ask your own version</ButtonText>
                </Button>
              </VStack>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
